import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import {
  Activity2Outline,
  Certificate2Icon,
  FlagOutline,
  GridOutline,
  MessageSquare2Outline,
  MeterOutline,
  TvOutline,
} from '@lyearn/icons';
import { TabType } from '@/components/Tabs/types';
import { SearchListEmptyStateLabelType } from '@/modules/corporate/components/SearchListEmptyState/types';
import { LearnerWidgetProps } from '@/modules/learner/views/LearnerProfile/types';
import routes from '@/pages/corporate/routes';
import { UserStatusEnum } from '@/types/schema';
import dynamic from '@/utils/dynamic';

export const TAB_SEARCH_PARAM = 'tab';

export enum LearnerProfileTabsEnum {
  Overview = 'overview',
  Content = 'content',
  Sessions = 'sessions',
  Certificates = 'certificates',
  OKRs = 'okrs',
  Skills = 'skills',
  Mindsets = 'Mindsets',
  SkillReviews = 'skillReviews',
  Activities = 'activities',
  OneOnOne = 'oneOnOne',
  About = 'about',
  Badges = 'Badges',
  Points = 'Points',
}

const LearnerHierarchy = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-hierarchy" */ '@/modules/learner/views/LearnerProfile/components/LearnerHierarchyWidget'
    ),
);

const LearnerCertificates = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-certificates" */ '@/modules/learner/views/LearnerProfile/components/LearnerCertificates'
    ),
);

const LearnerOverview = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-overview" */ '@/modules/learner/views/LearnerProfile/components/LearnerOverview'
    ),
);

const LearnerContents = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-contents" */ '@/modules/learner/views/LearnerProfile/components/LearnerContents'
    ),
);

const LearnerOKRs = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-okrs" */ '@/modules/learner/views/LearnerProfile/components/LearnerOKRs'
    ),
);

const LearnerSessions = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-sessions" */ '@/modules/learner/views/LearnerProfile/components/LearnerSessionsTable'
    ),
);

const LearnerSkillReviews = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-skill-reviews" */ '@/modules/learner/views/LearnerProfile/components/LearnerSkillReviews'
    ),
);

const LearnerActivities = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-activities" */ '@/modules/learner/views/LearnerProfile/components/LearnerActivities'
    ),
);

const LearnerOneOnOne = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-one-on-one" */ '@/modules/learner/views/LearnerProfile/components/LearnerOneOnOneTable'
    ),
);

const LearnerSkills = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-skills" */ '@/modules/user/views/UserProfile/views/EmployeeProfileSkills/components/SkillsTable'
    ),
);

const LearnerMindsets = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-mindsets" */ '@/modules/user/views/UserProfile/views/EmployeeProfileMindsets/components/MindsetsTable'
    ),
);

const LearnerBadges = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-badges" */ '@/modules/learner/views/LearnerProfile/components/LearnerBadges'
    ),
);

const LearnerPoints = dynamic(
  () =>
    import(
      /* webpackChunkName: "learner-points" */ '@/modules/learner/views/LearnerProfile/components/LearnerPoints'
    ),
);

export const LEARNER_PROFILE_TABS_CONFIG = {
  [LearnerProfileTabsEnum.Overview]: {
    key: LearnerProfileTabsEnum.Overview,
    labelKey: 'LearnerProfile:Overview',
    Component: LearnerOverview,
    EmptyState: {
      Icon: GridOutline,
      titleKey: 'LearnerProfile:Contents.NoOverview',
      descriptionKey: 'LearnerProfile:Contents.NoOverviewDescription',
      titleKeyInvited: 'LearnerProfile:Contents.NoOverviewInvited',
      descriptionKeyInvited: 'LearnerProfile:Contents.NoOverviewInvitedDescription',
    },
  },
  [LearnerProfileTabsEnum.Content]: {
    key: LearnerProfileTabsEnum.Content,
    labelKey: 'LearnerProfile:Contents.Content',
    Component: LearnerContents,
    EmptyState: {
      Icon: GridOutline,
      titleKey: 'LearnerProfile:Contents.NoContent',
      descriptionKey: 'LearnerProfile:Contents.NoContentDescription',
      titleKeyInvited: 'LearnerProfile:Contents.NoContentInvited',
      descriptionKeyInvited: 'LearnerProfile:Contents.NoContentInvitedDescription',
    },
  },
  [LearnerProfileTabsEnum.Sessions]: {
    key: LearnerProfileTabsEnum.Sessions,
    labelKey: 'Common:Sessions',
    Component: LearnerSessions,
    EmptyState: {
      Icon: TvOutline,
      titleKey: 'LearnerProfile:Sessions.NoSessions',
      descriptionKey: 'LearnerProfile:Sessions.NoSessionsDescription',
      titleKeyInvited: 'LearnerProfile:Sessions.NoSessionsInvited',
      descriptionKeyInvited: 'LearnerProfile:Sessions.NoSessionsInvitedDescription',
    },
  },
  [LearnerProfileTabsEnum.Certificates]: {
    key: LearnerProfileTabsEnum.Certificates,
    labelKey: 'LearnerProfile:Certificates.Certificates',
    Component: LearnerCertificates,
    EmptyState: {
      Icon: Certificate2Icon,
      titleKey: 'LearnerProfile:Certificates.NoCertificates',
      descriptionKey: 'LearnerProfile:Certificates.NoCertificatesDescription',
      titleKeyInvited: 'LearnerProfile:Certificates.NoCertificatesInvited',
      descriptionKeyInvited: 'LearnerProfile:Certificates.NoCertificatesInvitedDescription',
    },
  },
  [LearnerProfileTabsEnum.OKRs]: {
    key: LearnerProfileTabsEnum.OKRs,
    labelKey: 'Common:Routes.OKRs',
    Component: LearnerOKRs,
    EmptyState: {
      Icon: FlagOutline,
      titleKey: 'LearnerProfile:OKRs.NoOKRs',
      descriptionKey: 'LearnerProfile:OKRs.NoOKRsDescription',
      titleKeyInvited: 'LearnerProfile:OKRs.NoOKRs',
      descriptionKeyInvited: 'LearnerProfile:OKRs.NoOKRsInvitedDescription',
    },
  },
  [LearnerProfileTabsEnum.Skills]: {
    key: LearnerProfileTabsEnum.Skills,
    labelKey: 'Common:RoleDetails.Skills',
    Component: ({ learner, isCurrentUser }: LearnerWidgetProps) => (
      <LearnerSkills isCurrentUser={isCurrentUser!} showHeader={false} user={learner} />
    ),
  },
  [LearnerProfileTabsEnum.Mindsets]: {
    key: LearnerProfileTabsEnum.Mindsets,
    labelKey: 'Common:RoleDetails.Mindsets',
    Component: ({ learner, isCurrentUser }: LearnerWidgetProps) => (
      <LearnerMindsets isCurrentUser={isCurrentUser!} showHeader={false} user={learner} />
    ),
  },
  [LearnerProfileTabsEnum.SkillReviews]: {
    key: LearnerProfileTabsEnum.SkillReviews,
    labelKey: 'LearnerProfile:SkillReviews.SkillReviews',
    Component: LearnerSkillReviews,
    EmptyState: {
      Icon: MeterOutline,
      titleKey: 'LearnerProfile:SkillReviews.NoSkillReviews',
      descriptionKey: 'LearnerProfile:SkillReviews.NoSkillReviewsDescription',
      titleKeyInvited: 'LearnerProfile:SkillReviews.NoSkillReviewsInvited',
      descriptionKeyInvited: 'LearnerProfile:SkillReviews.NoSkillReviewsInvitedDescription',
      className: 'whitespace-pre-line',
    },
  },
  [LearnerProfileTabsEnum.OneOnOne]: {
    key: LearnerProfileTabsEnum.OneOnOne,
    labelKey: 'Common:Routes.1on1',
    Component: LearnerOneOnOne,
    EmptyState: {
      Icon: MessageSquare2Outline,
      titleKey: 'LearnerProfile:OneOnOne.NoOneOnOne',
      descriptionKey: 'LearnerProfile:OneOnOne.NoOneOnOneDescription',
      titleKeyInvited: 'LearnerProfile:OneOnOne.NoOneOnOne',
      descriptionKeyInvited: 'LearnerProfile:OneOnOne.NoOneOnOneDescription',
      className: 'whitespace-pre-line',
    },
  },
  [LearnerProfileTabsEnum.Activities]: {
    key: LearnerProfileTabsEnum.Activities,
    labelKey: 'LearnerProfile:Activities.Activities',
    Component: LearnerActivities,
    EmptyState: {
      Icon: Activity2Outline,
      titleKey: 'LearnerProfile:Activities.NoActivities',
      descriptionKey: 'LearnerProfile:Activities.NoActivitiesDescription',
      titleKeyInvited: 'LearnerProfile:Activities.NoActivities',
      descriptionKeyInvited: 'LearnerProfile:Activities.NoActivitiesInvitedDescription',
    },
  },
  [LearnerProfileTabsEnum.About]: {
    key: LearnerProfileTabsEnum.About,
    labelKey: 'Common:About',
    Component: LearnerHierarchy,
    EmptyState: {},
  },
  [LearnerProfileTabsEnum.Badges]: {
    key: LearnerProfileTabsEnum.Badges,
    labelKey: 'Common:Badges',
    Component: LearnerBadges,
    EmptyState: {},
  },
  [LearnerProfileTabsEnum.Points]: {
    key: LearnerProfileTabsEnum.Points,
    labelKey: 'Common:Points',
    Component: LearnerPoints,
    EmptyState: {},
  },
};

interface GetEntityEmptyStatePropsParams {
  t: TFunction;
  tab:
    | LearnerProfileTabsEnum.Activities
    | LearnerProfileTabsEnum.Certificates
    | LearnerProfileTabsEnum.Content
    | LearnerProfileTabsEnum.OKRs
    | LearnerProfileTabsEnum.OneOnOne
    | LearnerProfileTabsEnum.Sessions
    | LearnerProfileTabsEnum.SkillReviews;
  status?: UserStatusEnum;
}

type EntityEmptyStateProps = SearchListEmptyStateLabelType;

export const getEntityEmptyStateProps = (
  params: GetEntityEmptyStatePropsParams,
): EntityEmptyStateProps => {
  const { t, tab, status } = params;
  const isInvited = status === UserStatusEnum.Invited;
  const config = LEARNER_PROFILE_TABS_CONFIG[tab].EmptyState;

  return {
    Icon: config.Icon,
    title: t(isInvited ? config.titleKeyInvited : config.titleKey),
    description: t(isInvited ? config.descriptionKeyInvited : config.descriptionKey),
  };
};

interface Params {
  learnerId: string;
  t: TFunction;
  isAdmin: boolean;
  isLearnerProfileTabEnabled: (tab: LearnerProfileTabsEnum) => boolean;
}

export const getLearnerProfileTabs = ({
  learnerId,
  t,
  isAdmin,
  isLearnerProfileTabEnabled,
}: Params): TabType<LearnerProfileTabsEnum>[] => {
  const getRoute = isAdmin ? routes.AdminLearnerProfile : routes.LearnerProfile;

  return getEnabledItems(
    Object.values(LEARNER_PROFILE_TABS_CONFIG).map(({ labelKey, key }) => ({
      value: key,
      label: t(labelKey),
      to: `${getRoute(learnerId)}?${TAB_SEARCH_PARAM}=${key}`,
      enabled: isLearnerProfileTabEnabled(key),
    })),
  );
};
